import { client, updateImporterSettings } from './api';
import { filenameFromDisposition } from '../utils/downloadFile';

/** One queued patient. Mirrors `ImporterQueueRow` in `app/api/importer.py`. */
export type EmptyReferralPolicy = 'skip' | 'patient_only';

export interface ImporterQueueRow {
  import_outcome?: string | null;
  import_summary?: string[] | null;
  id: string;
  status: string;
  /** `{target agency}:{source agency}:{credential}` — the lane this row is in. */
  queue_key: string;
  source_agency_id: string;
  mrn: string | null;
  patient_key: string | null;
  created_at: string;
  claimed_at: string | null;
  /** When the chart settled. Null while queued or importing. */
  ended_at: string | null;
  not_before: string | null;
  error: string | null;
  /** The login that claimed this chart. Null until claimed — a pooled row
   *  belongs to no login until one takes it. */
  credential_id: string | null;
  /** The landed chart is a second copy of one this agency already had. */
  duplicate_import?: boolean;
  /** A print retry, not a chart: how many lost print views it asks for
   *  again, and which try this is. Null on a chart import. */
  print_retry_task_count?: number | null;
  print_retry_attempt?: number | null;
  /** A nightly task-list check, not a chart: how many watched charts it
   *  reads. Null on every other row. */
  probe_target_count?: number | null;
}

export interface ImporterLaneCounts {
  queued: number;
  running: number;
  completed: number;
  failed: number;
  canceled: number;
}

export interface ImporterLaneBreaker {
  is_open: boolean;
  opened_at: string | null;
  probe_after: string | null;
  reason: string | null;
}

/**
 * One utility login working a pool, and why it is or is not moving. Mirrors
 * `ImporterCredentialLane`.
 *
 * `is_paused` and `breaker.is_open` are two different sentences and are never
 * folded into one: the first says somebody here stopped this login, the second
 * says the old EMR refused it. They are undone differently — one by resuming,
 * the other by fixing a password. Both can be true at once, and the login
 * claims nothing while either is.
 */
export interface ImporterCredentialLane {
  credential_id: string | null;
  /** What Roger Docs calls this login. Null when it has no stored label. */
  credential_label: string | null;
  /** What the pause and reset routes address this login by — not `queue_key`. */
  lane_key: string;
  /** 0 or 1: one chart per login is the invariant the queue exists to keep. */
  running: number;
  /** An operator stopped this login. Never a failure. */
  is_paused: boolean;
  paused_at: string | null;
  breaker: ImporterLaneBreaker;
  /**
   * This login's own pace, over the same window as the pool's — which is why
   * a pool's rate is exactly its logins' added up. Null when this login has
   * settled nothing in the window: no measurement, which is not zero.
   */
  settled_in_window: number;
  charts_per_hour: number | null;
}

export interface ImporterLane {
  queue_key: string;
  source_agency_id: string | null;
  /** Null on a pool spanning several logins — the logins are in `credential_lanes`. */
  credential_id: string | null;
  credential_label: string | null;
  counts: ImporterLaneCounts;
  /** Charts the sequencer will still pick up by itself: queued plus running. */
  remaining: number;
  /** The pool's, and open only when **every** login here is refused. */
  breaker: ImporterLaneBreaker;
  credential_lanes: ImporterCredentialLane[];
  settled_in_window: number;
  rate_window_minutes: number;
  /** Null when the lane has settled nothing in the window — no rate to show. */
  charts_per_hour: number | null;
  eta_seconds: number | null;
}

export interface ImporterQueue {
  agency_id: string;
  lanes: ImporterLane[];
  items: ImporterQueueRow[];
  /**
   * Where the next page of finished charts starts, or null at the end of the
   * list. It is the last row's `updated_at`, not its `created_at`.
   */
  next_before: string | null;
}

export type ImporterQueueStatusFilter = 'failed' | 'completed';

/**
 * Which page of the queue to read.
 *
 * The first page carries the lanes and **every** live row, because the work
 * still outstanding is what the page exists to show; `limit` governs only the
 * finished rows that follow. A page asked for `before` a cursor is finished
 * rows alone, so pages are appended rather than swapped.
 *
 * When `status` is `failed` or `completed`, the server returns only rows in
 * that status (newest first), with no live rows — use it to walk every failure
 * or every successful import in a large capture.
 */
export interface ImporterQueuePage {
  /** Finished rows per page. The server accepts 1–200 and defaults to 100. */
  limit?: number;
  /** A previous page's `next_before`. Omit for the first page. */
  before?: string;
  status?: ImporterQueueStatusFilter;
}

export async function getImporterQueue(
  page: ImporterQueuePage = {}
): Promise<ImporterQueue> {
  const query: Record<string, string | number> = {};
  if (page.limit !== undefined) query.limit = page.limit;
  if (page.before !== undefined) query.before = page.before;
  if (page.status !== undefined) query.status = page.status;
  const response = await client.get<ImporterQueue, unknown, true>({
    query,
    responseType: 'json',
    throwOnError: true,
    url: '/importer/queue',
  });
  return response.data;
}

/**
 * What one "retry all failed" press did to a lane. Mirrors
 * `ImporterRetryFailedResult`.
 *
 * `examined === retried + already_queued + canceled`, and
 * `examined + over_limit === failed_before`. The two skip counts are rows the
 * server looked at and deliberately left failed; `over_limit` is the rows the
 * cap never reached, which pressing again does take.
 */
export interface ImporterRetryFailedResult {
  queue_key: string;
  limit: number;
  failed_before: number;
  examined: number;
  retried: number;
  already_queued: number;
  canceled: number;
  over_limit: number;
  sequencer_kicked: boolean;
}

/** Put one failed chart back in its lane's queue. Resolves to the reopened row. */
export async function retryImporterQueueRow(
  itemId: string
): Promise<ImporterQueueRow> {
  const response = await client.post<ImporterQueueRow, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/importer/queue/${itemId}/retry`,
  });
  return response.data;
}

/** Reopen a bounded page of one lane's failed charts. */
export async function retryImporterLaneFailures(
  queueKey: string
): Promise<ImporterRetryFailedResult> {
  const response = await client.post<ImporterRetryFailedResult, unknown, true>({
    body: { queue_key: queueKey },
    responseType: 'json',
    throwOnError: true,
    url: '/importer/queue/retry-failed',
  });
  return response.data;
}

/** One tab of an uploaded roster. Mirrors `ImporterRosterSheet`. */
export interface ImporterRosterSheet {
  name: string;
  rows: number;
  /** False when the sheet has no Medical Record Key column, so nothing on it was queued. */
  read: boolean;
}

/** One roster row that did not become a queue row. Carries no cell content. */
export interface ImporterRosterRowNote {
  sheet: string;
  row_number: number;
  reason: string;
  /** The row of this same upload it collapsed onto, when that is where it went. */
  collapsed_onto_row: number | null;
}

/**
 * What one page of an uploaded roster did to the queue. Mirrors
 * `ImporterRosterResult`.
 *
 * `rows_processed === enqueued + duplicates + rejected`, always: the import is
 * keyed per patient and the roster is written per episode, so the rows that
 * collapse have to be counted somewhere other than `enqueued`.
 *
 * The server queues a bounded number of rows per request — the real 12,938-row
 * file takes minutes, and the load balancer closes a connection idle for a
 * minute — so `next_offset` says where to resume, and is null when the file is
 * done.
 */
export interface ImporterRosterResult {
  source_agency_id: string;
  queue_key: string;
  sheets: ImporterRosterSheet[];
  rows_in_file: number;
  offset: number;
  next_offset: number | null;
  rows_processed: number;
  enqueued: number;
  // Of `enqueued`, the patients queued for episode prints rather than task
  // prints. Under the automatic print mode that is the ones whose latest
  // episode ended over sixty days ago; a chosen mode makes it all or none.
  enqueued_episode_prints: number;
  // Rows whose Medical Record Key has a year-variant elsewhere in the file
  // naming the same patient, so their queue item also asks the scrape about
  // that key's episodes. WellSky re-issues a key with the current year on
  // some re-admissions. No row is merged away — every key keeps its own queue
  // item, because the roster cannot tell a re-issued key from a second
  // patient record opened beside the first — so this cuts across the buckets
  // rather than being one.
  rows_with_sibling_mrns: number;
  // Rows from a tab that carried no Episode Start column. Their Medical
  // Record Keys are never grouped with a year-variant: showing that two keys
  // were never live at the same time needs both ends of a certification
  // period, and without it only the names are left — which is the one thing
  // year-variant twins agree on. Cross-cutting too; the row is queued as
  // usual.
  rows_without_episode_start: number;
  // Rows that were already waiting in the queue and that this upload changed
  // rather than queued. With Overwrite ticked, a waiting row takes it along
  // with the roster data that has to come with it — the roster-admissions
  // flag and this file's episode end dates — because a row queued from the
  // internal tool carries neither, and Overwrite without them rebuilds the
  // chart from its latest stay alone. Counted in `duplicates` like any other
  // waiting row, so this is the only thing that says one was touched; zero on
  // every upload without the box.
  waiting_rows_updated: number;
  duplicates: number;
  rejected: number;
  duplicate_rows: ImporterRosterRowNote[];
  rejected_rows: ImporterRosterRowNote[];
  detail_truncated: boolean;
  sequencer_kicked: boolean;
}

/**
 * How the roster's charts are printed at scrape time. Mirrors `RosterPrintMode`.
 *
 * `auto` is the rule the capture shipped with: one chart PDF per episode for a
 * patient whose latest episode ended over sixty days ago, a PDF per task for
 * everyone else. `task` and `episode` give every row of the upload that one
 * kind. Claims, fall and infection records come across either way.
 */
export type ImporterRosterPrintMode = 'auto' | 'task' | 'episode';

/**
 * Queue one page of a roster.
 *
 * `overwriteExisting` asks every row of the page to rebuild the chart Roger
 * already holds for that patient rather than be refused for it. The server
 * takes it only from a super-user, and refuses the whole page otherwise, so
 * send it only when the page is showing that choice.
 */
export async function uploadImporterRosterPage(
  file: File,
  offset: number,
  printMode: ImporterRosterPrintMode = 'auto',
  overwriteExisting = false,
  emptyReferralPolicy?: EmptyReferralPolicy
): Promise<ImporterRosterResult> {
  const body = new FormData();
  body.append('file', file);
  body.append('offset', String(offset));
  body.append('print_mode', printMode);
  if (emptyReferralPolicy)
    body.append('empty_referral_policy', emptyReferralPolicy);

  body.append('overwrite_existing', String(overwriteExisting));
  const response = await client.post<ImporterRosterResult, unknown, true>({
    body,
    responseType: 'json',
    throwOnError: true,
    url: '/importer/roster',
  });
  return response.data;
}

/**
 * The operator's own roster handed back with Roger's status columns on it.
 *
 * The workbook is the answer, so the tally the page shows beside the download
 * rides on the response headers rather than in a body this could read. Its
 * four numbers are counted over the whole file: `rows` is every row of every
 * readable sheet, and the other three are `roger_episode_landed` outcomes —
 * the episode is in Roger, the chart is here without it, and the patient was
 * never imported. `landed` counts an episode found under a year-variant of
 * the row's own medical record number too, because the operator asked whether
 * the stay is here; the workbook's summary tab counts the two apart. They do
 * not add up to `rows`, because a row whose end date Roger could not read is
 * none of the three.
 */
export interface ImporterRosterStatusReport {
  blob: Blob;
  /** What the server called the file: the uploaded name plus `-status.xlsx`. */
  filename: string;
  rows: number;
  landed: number;
  missing: number;
  notImported: number;
}

function headerCount(headers: unknown, name: string): number {
  const value = (headers as Record<string, unknown> | undefined)?.[name];
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Read the queue back a row of the roster at a time.
 *
 * Takes the same workbook the queue was uploaded from and queues nothing: the
 * server matches each row to its own queue item and answers in a copy of the
 * file. One request for the whole roster, unlike `uploadImporterRosterPage`,
 * because nothing is written and a request that dies can simply be made again.
 */
export async function downloadImporterRosterStatus(
  file: File
): Promise<ImporterRosterStatusReport> {
  const body = new FormData();
  body.append('file', file);
  const response = await client.post<Blob, unknown, true>({
    body,
    responseType: 'blob',
    throwOnError: true,
    url: '/importer/roster/status',
  });
  return {
    blob: response.data,
    filename: filenameFromDisposition(
      response.headers?.['content-disposition'],
      'roster-status.xlsx'
    ),
    rows: headerCount(response.headers, 'x-roster-rows'),
    landed: headerCount(response.headers, 'x-roster-landed'),
    missing: headerCount(response.headers, 'x-roster-missing'),
    notImported: headerCount(response.headers, 'x-roster-not-imported'),
  };
}

/**
 * What this agency's capture is allowed to run on. Mirrors `ImporterSettings`.
 *
 * `max_concurrent_lanes` is the *effective* number, not the stored one: with
 * nothing stored it is every valid utility login Roger Docs holds, and
 * `is_default` says which of the two is being read.
 */
export interface ImporterSettings {
  max_concurrent_lanes: number;
  /** True while nothing is stored and the number shown is the login count. */
  is_default: boolean;
  /** Valid utility logins Roger Docs holds. 0 means nothing can import. */
  valid_credentials: number;
  /** Whether the whole capture is stopped by hand. */
  is_paused?: boolean;
  /**
   * Charts each login may import at once. 1 unless an operator raised it.
   */
  charts_per_login: number;
  /**
   * Kinnser sessions each chart holds on its login, its own included. Set by
   * deploy; absent from a server that predates it, which means 1.
   */
  sessions_per_chart?: number;
}

/** The range the server's CHECK constraint accepts. */
export const MIN_CONCURRENT_LANES = 1;
export const MAX_CONCURRENT_LANES = 25;
/** The same, for charts per login. */
export const MIN_CHARTS_PER_LOGIN = 1;
export const MAX_CHARTS_PER_LOGIN = 50;

export async function getImporterSettings(): Promise<ImporterSettings> {
  const response = await client.get<ImporterSettings, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: '/importer/settings',
  });
  return response.data;
}

/**
 * Change how many logins may work this agency's capture at once.
 *
 * Live in both directions, and neither direction interrupts a chart: raising
 * starts the extra lanes when the pool is next kicked, and lowering only stops
 * the lanes over the new number from claiming again once they have settled the
 * chart they are holding.
 */
export async function setImporterMaxConcurrentLanes(
  maxConcurrentLanes: number
): Promise<ImporterSettings> {
  const response = await client.put<ImporterSettings, unknown, true>({
    body: { max_concurrent_lanes: maxConcurrentLanes },
    responseType: 'json',
    throwOnError: true,
    url: '/importer/settings',
  });
  return response.data;
}

/**
 * Change how many charts each login may import at once.
 *
 * Live in both directions. Raising it lets a login claim another chart on its
 * next tick; lowering it stops a login over the number from claiming until
 * enough of its charts have finished. Nothing in flight is interrupted.
 */
export async function setImporterChartsPerLogin(
  chartsPerLogin: number
): Promise<ImporterSettings> {
  const response = await client.put<ImporterSettings, unknown, true>({
    body: { charts_per_login: chartsPerLogin },
    responseType: 'json',
    throwOnError: true,
    url: '/importer/settings',
  });
  return response.data;
}

/** Stop or restart this agency's whole capture. Resuming kicks every live pool. */
export async function setImporterCapturePaused(
  isPaused: boolean
): Promise<ImporterSettings> {
  const { data } = await updateImporterSettings({
    body: { is_paused: isPaused },
    throwOnError: true,
  });
  return { ...data, charts_per_login: data.charts_per_login ?? 1 };
}

/**
 * Stop one login from claiming another chart, or let it claim again.
 *
 * Addressed by `lane_key`, not `queue_key`: a pool has one queue key and as
 * many lanes as the agency has logins. Neither call touches the chart the
 * login is holding — pausing stops the *next* claim, so stopping a login is
 * never abandoning a patient half-scraped.
 */
export async function pauseImporterLane(
  laneKey: string
): Promise<ImporterCredentialLane> {
  const response = await client.post<ImporterCredentialLane, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/importer/lanes/${encodeURIComponent(laneKey)}/pause`,
  });
  return response.data;
}

export async function resumeImporterLane(
  laneKey: string
): Promise<ImporterCredentialLane> {
  const response = await client.post<ImporterCredentialLane, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/importer/lanes/${encodeURIComponent(laneKey)}/resume`,
  });
  return response.data;
}

/**
 * Take the breaker's cooldown probe early.
 *
 * The breaker reopens itself after half an hour by letting one claim through.
 * That is right when nobody is watching and wrong when somebody has just fixed
 * the password, so this is the same probe, now. If the login is still refused,
 * the next few charts trip it straight back.
 */
export async function resetImporterLaneBreaker(
  laneKey: string
): Promise<ImporterCredentialLane> {
  const response = await client.post<ImporterCredentialLane, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/importer/lanes/${encodeURIComponent(laneKey)}/reset-breaker`,
  });
  return response.data;
}

/**
 * What one reset page did to this agency's capture. Mirrors
 * `ImporterResetResult`.
 *
 * The server cancels live jobs and empties the queue on every call, then
 * deletes a bounded page of imported patients. `done` is true when no
 * imported chart remains; call again until it is.
 */
export interface ImporterResetResult {
  canceled_jobs: number;
  queue_rows_deleted: number;
  patients_deleted: number;
  patients_remaining: number;
  patients_protected: number;
  done: boolean;
  stalled: boolean;
  restore_capture_pause: boolean;
}

export interface ImporterResetRequest {
  restore_capture_pause?: boolean;
}

export async function resetAgencyImport(
  body: ImporterResetRequest = {}
): Promise<ImporterResetResult> {
  const response = await client.post<
    ImporterResetResult,
    ImporterResetRequest,
    true
  >({
    responseType: 'json',
    throwOnError: true,
    url: '/importer/reset',
    body,
  });
  return response.data;
}
